import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  useTheme,
  alpha,
  Paper,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  Menu as MenuIcon,
  ChevronLeft,
  ChevronRight,
  ListAlt,
  Settings,
  Assessment,
  Logout,
  Dashboard as DashboardIcon,
  AddCircle,
  LocalOffer,
  FormatListBulleted,
  RateReview,
} from "@mui/icons-material";
import Header from "../Components/Header";
import Footer from "../Components/Footer";

const drawerWidth = 280;

const AaasDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [open, setOpen] = useState(true);

  const GOLD = "#D4AF37";

  const menuItems = [
    {
      name: "Schedule Exam",
      icon: <ListAlt />,
      path: "/db-shiksha/kaas/admin/scheduleExam",
    },
    {
      name: "Question Bank Setup",
      icon: <Settings />,
      path: "/db-shiksha/kaas/admin/profile",
    },
    {
      name: "Result",
      icon: <Assessment />,
      path: "/db-shiksha/kaas/admin/result",
    },
  ];

  const handleToggleDrawer = () => {
    setOpen(!open);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "background.default" }}>
      <Header />
      
      <Box sx={{ display: "flex", flexGrow: 1, mt: "64px" }}>
        {/* Sidebar */}
        <Drawer
          variant="permanent"
          open={open}
          sx={{
            width: open ? drawerWidth : 80,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: open ? drawerWidth : 80,
              top: "64px",
              height: "calc(100vh - 64px)",
              transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: "hidden",
              borderRight: "1px solid",
              borderColor: "divider",
              boxShadow: "4px 0 10px rgba(0,0,0,0.05)",
            },
          }}
        >
          <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
            <IconButton onClick={handleToggleDrawer}>
              {open ? <ChevronLeft /> : <ChevronRight />}
            </IconButton>
          </Box>
          <Divider sx={{ mx: 2 }} />
          
          <List sx={{ px: 2, py: 3 }}>
            {menuItems.map((item) => (
              <ListItem key={item.name} disablePadding sx={{ display: "block", mb: 1 }}>
                <Tooltip title={!open ? item.name : ""} placement="right">
                  <ListItemButton
                    component={Link}
                    to={item.path}
                    sx={{
                      minHeight: 52,
                      justifyContent: open ? "initial" : "center",
                      px: 2.5,
                      borderRadius: "16px",
                      "&:hover": {
                        bgcolor: alpha(GOLD, 0.1),
                        "& .MuiListItemIcon-root": { color: GOLD },
                        "& .MuiListItemText-primary": { color: GOLD, fontWeight: 700 },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : "auto",
                        justifyContent: "center",
                        transition: "color 0.2s",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.name}
                      sx={{
                        opacity: open ? 1 : 0,
                        "& .MuiTypography-root": {
                          fontWeight: 600,
                          fontSize: "0.95rem",
                          transition: "color 0.2s",
                        },
                      }}
                    />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        </Drawer>

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 4,
            width: open ? `calc(100% - ${drawerWidth}px)` : `calc(100% - 80px)`,
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            bgcolor: "#f8f9fa",
          }}
        >
          <Container maxWidth="xl">
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h3"
                fontWeight={900}
                sx={{
                  background: `linear-gradient(45deg, #1A1A1A 30%, ${GOLD} 90%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 1,
                }}
              >
                Aaas Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage your exams, question banks, and results from one central hub.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 4,
                    borderRadius: "24px",
                    textAlign: "center",
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "0.3s",
                    cursor: "pointer",
                    "&:hover": { transform: "translateY(-10px)", boxShadow: "0 20px 40px rgba(0,0,0,0.1)" },
                  }}
                  onClick={() => navigate("/db-shiksha/kaas/admin/scheduleExam")}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: alpha(GOLD, 0.1),
                      color: GOLD,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 3,
                    }}
                  >
                    <ListAlt sx={{ fontSize: 40 }} />
                  </Box>
                  <Typography variant="h5" fontWeight={800} gutterBottom>
                    Exams
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Schedule and monitor your certification exams.
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 4,
                    borderRadius: "24px",
                    textAlign: "center",
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "0.3s",
                    cursor: "pointer",
                    "&:hover": { transform: "translateY(-10px)", boxShadow: "0 20px 40px rgba(0,0,0,0.1)" },
                  }}
                  onClick={() => navigate("/db-shiksha/kaas/admin/profile")}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: alpha(GOLD, 0.1),
                      color: GOLD,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 3,
                    }}
                  >
                    <Settings sx={{ fontSize: 40 }} />
                  </Box>
                  <Typography variant="h5" fontWeight={800} gutterBottom>
                    Question Bank
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Set up and manage your assessment question repositories.
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 4,
                    borderRadius: "24px",
                    textAlign: "center",
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "0.3s",
                    cursor: "pointer",
                    "&:hover": { transform: "translateY(-10px)", boxShadow: "0 20px 40px rgba(0,0,0,0.1)" },
                  }}
                  onClick={() => navigate("/db-shiksha/kaas/admin/result")}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: alpha(GOLD, 0.1),
                      color: GOLD,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 3,
                    }}
                  >
                    <Assessment sx={{ fontSize: 40 }} />
                  </Box>
                  <Typography variant="h5" fontWeight={800} gutterBottom>
                    Results
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View detailed analytics and scores for all your exams.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
};

export default AaasDashboard;
